/**
 * Regions Transformers
 * Maps between API DTOs and Domain Models
 *
 * Pattern:
 * - API layer speaks DTOs (external contract from Strapi)
 * - Domain layer speaks Models (internal contract)
 * - Transformers convert between them
 * - Keeps domain pure from API concerns
 */

import type {
  Region,
  RegionListItem,
  RegionsFilter,
  PaginatedRegions,
  CreateRegionRequest,
  UpdateRegionRequest,
  CountrySummary,
  FactorySummary,
  CitySummary,
} from '../domain/types'
import type {
  RegionDto,
  CreateRegionApiRequest,
  UpdateRegionApiRequest,
  RegionListResponse,
  CountryDto,
  FactoryDto,
  CityDto,
} from './api-types'

// ===========================================
// RELATED ENTITY TRANSFORMERS
// ===========================================

/**
 * Transform Country DTO to domain summary
 */
function transformCountryDtoToDomain(dto: CountryDto): CountrySummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    isActive: dto.isActive ?? true,
  }
}

/**
 * Transform Factory DTO to domain summary
 */
function transformFactoryDtoToDomain(dto: FactoryDto): FactorySummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name, // Strapi returns name as string when populated through relations
    status: dto.factoryStatus ?? 'active',
  }
}

/**
 * Transform City DTO to domain summary
 */
function transformCityDtoToDomain(dto: CityDto): CitySummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    isActive: dto.isActive ?? true,
  }
}

// ===========================================
// DTO → DOMAIN TRANSFORMERS
// ===========================================

/**
 * Transform API DTO to Domain Model
 * Called after fetching from API
 */
export function transformDtoToDomain(dto: RegionDto): Region {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    description: dto.description ?? undefined,
    isActive: dto.isActive ?? true,
    // Transform relations
    country: dto.country ? transformCountryDtoToDomain(dto.country) : undefined,
    factories: dto.factories?.map(transformFactoryDtoToDomain) ?? undefined,
    cities: dto.cities?.map(transformCityDtoToDomain) ?? undefined,
    // Metadata
    metadata: dto.metadata ?? undefined,
    // Timestamps
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform API DTO to RegionListItem (UI-friendly)
 */
export function transformDtoToListItem(dto: RegionDto): RegionListItem {
  const factories = dto.factories ?? []
  const cities = dto.cities ?? []

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    description: dto.description ?? undefined,
    isActive: dto.isActive ?? true,
    // Country info
    countryId: dto.country ? String(dto.country.id) : undefined,
    countryCode: dto.country?.code,
    countryName: dto.country?.name,
    // Factory info
    factoryCount: factories.length,
    factoryNames: factories.map(f => f.name), // Strapi returns name as string
    // City info
    cityCount: cities.length,
    // Timestamps
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform list of DTOs to Domain Models
 */
export function transformDtoListToDomain(dtos: RegionDto[]): Region[] {
  return dtos.map(transformDtoToDomain)
}

/**
 * Transform list of DTOs to RegionListItems
 */
export function transformDtoListToListItems(dtos: RegionDto[]): RegionListItem[] {
  return dtos.map(transformDtoToListItem)
}

/**
 * Transform paginated API response to domain format
 */
export function transformListResponseToDomain(response: RegionListResponse): PaginatedRegions {
  return {
    data: transformDtoListToListItems(response.data),
    pagination: response.meta.pagination,
  }
}

// ===========================================
// DOMAIN → DTO TRANSFORMERS
// ===========================================

/**
 * Transform Domain CreateRegionRequest to API request
 */
export function transformToCreateApiRequest(data: CreateRegionRequest): CreateRegionApiRequest {
  return {
    code: data.code,
    name: data.name,
    description: data.description,
    isActive: data.isActive ?? true,
    country: data.countryId,
    factories: data.factoryIds,
    metadata: data.metadata,
  }
}

/**
 * Transform Domain UpdateRegionRequest to API request
 */
export function transformToUpdateApiRequest(data: UpdateRegionRequest): UpdateRegionApiRequest {
  const request: UpdateRegionApiRequest = {}

  if (data.name !== undefined) {
    request.name = data.name
  }

  if (data.description !== undefined) {
    request.description = data.description
  }

  if (data.isActive !== undefined) {
    request.isActive = data.isActive
  }

  if (data.countryId !== undefined) {
    request.country = data.countryId
  }

  if (data.factoryIds !== undefined) {
    request.factories = data.factoryIds
  }

  if (data.metadata !== undefined) {
    request.metadata = data.metadata
  }

  return request
}

// ===========================================
// FILTER TRANSFORMERS
// ===========================================

/**
 * Transform Domain Filter to Strapi Query Params
 */
export function transformFilterToQueryParams(
  filter: RegionsFilter
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {}

  // Pagination
  if (filter.page !== undefined) {
    params['pagination[page]'] = filter.page
  }
  if (filter.pageSize !== undefined) {
    params['pagination[pageSize]'] = filter.pageSize
  }

  // Filters
  if (filter.isActive !== undefined && filter.isActive !== 'all') {
    params['filters[isActive][$eq]'] = filter.isActive
  }

  if (filter.search) {
    params['filters[$or][0][name][$containsi]'] = filter.search
    params['filters[$or][1][code][$containsi]'] = filter.search
  }

  if (filter.countryId) {
    params['filters[country][documentId][$eq]'] = filter.countryId
  }

  // Sorting
  params['sort'] = 'name:asc'

  return params
}

// ===========================================
// FORM DATA TRANSFORMERS
// ===========================================

/**
 * Transform form data to CreateRegionRequest
 */
export function transformFormToCreateRequest(
  formData: {
    code: string
    name: string
    description?: string
    isActive?: boolean
    countryId?: string
    factoryIds?: string[]
  }
): CreateRegionRequest {
  return {
    code: formData.code,
    name: formData.name,
    description: formData.description,
    isActive: formData.isActive ?? true,
    countryId: formData.countryId,
    factoryIds: formData.factoryIds,
  }
}

/**
 * Transform form data to UpdateRegionRequest
 */
export function transformFormToUpdateRequest(
  formData: {
    name?: string
    description?: string
    isActive?: boolean
    countryId?: string | null
    factoryIds?: string[]
  }
): UpdateRegionRequest {
  return {
    name: formData.name,
    description: formData.description,
    isActive: formData.isActive,
    countryId: formData.countryId ?? undefined,
    factoryIds: formData.factoryIds,
  }
}

/**
 * Transform Region to form data for editing
 */
export function transformRegionToFormData(
  region: Region
): {
  code: string
  name: string
  description: string
  isActive: boolean
  countryId: string
  factoryIds: string[]
} {
  return {
    code: region.code,
    name: region.name,
    description: region.description ?? '',
    isActive: region.isActive,
    countryId: region.country?.documentId ?? '',
    factoryIds: region.factories?.map(f => f.documentId) ?? [],
  }
}

// ===========================================
// UTILITY TRANSFORMERS
// ===========================================

/**
 * Extract document IDs from regions
 */
export function extractDocumentIds(regions: Region[]): string[] {
  return regions.map(r => r.documentId)
}

/**
 * Create a lookup map by documentId
 */
export function createLookupMap(regions: Region[]): Map<string, Region> {
  return new Map(regions.map(r => [r.documentId, r]))
}
